import type { StudentQuotaMeResponse } from '../../modules/tutor/api/quota.client';
import type { TutorUsageFinalizeResult } from '../api/tutorUsageApi';

/**
 * Quota Runtime V1 client helpers (daily + monthly Start Class / countdown).
 *
 * KEEP while ``quota_runtime_v2_enabled`` can be false.
 * Under V2, Start Class must use ``isRuntimeV2StartBlocked`` / ``/runtime/*`` — do not extend
 * daily-gated paths here. See docs/ai-quota-runtime-v2-legacy-cleanup.md.
 */
export type TutorQuotaDailyFields = Pick<
  StudentQuotaMeResponse,
  | 'daily_used_seconds'
  | 'daily_allocated_seconds'
  | 'daily_remaining_seconds'
  | 'usage_scope'
  | 'governance_mode'
  | 'channel_daily_used_seconds'
  | 'channel_daily_limit_seconds'
  | 'channel_monthly_used_seconds'
  | 'channel_monthly_limit_seconds'
>;

export type ExperienceQuotaFields = Pick<
  StudentQuotaMeResponse,
  | 'feature_disabled'
  | 'quota_exhausted'
  | 'usage_scope'
  | 'governance_mode'
  | 'daily_used_seconds'
  | 'daily_allocated_seconds'
  | 'daily_remaining_seconds'
  | 'monthly_remaining_seconds'
  | 'monthly_allocated_seconds'
  | 'monthly_used_seconds'
  | 'channel_daily_used_seconds'
  | 'channel_daily_limit_seconds'
  | 'channel_monthly_used_seconds'
  | 'channel_monthly_limit_seconds'
  | 'daily_remaining_minutes'
  | 'monthly_remaining_minutes'
  | 'preferred_allocation'
>;

export type ExperienceQuotaDisplay = {
  usedSeconds: number;
  remainingSeconds: number | null;
  allocatedSeconds: number | null;
};

export type TutorSessionQuotaSnapshot = {
  usedAtStart: number;
  allocatedAtStart: number | null;
  /** Null = flexible governance with no client-side session cap (server checkpoints enforce limits). */
  remainingAtStart: number | null;
  sessionStartMs: number;
};

export type ExperienceBillingStartParams = {
  canStart: boolean;
  sessionCapSeconds: number | null;
  remainingForStartApi: number | null;
  governanceMode: 'flexible' | 'strict' | null;
  blockReason: 'exhausted' | 'unknown_remaining' | null;
};

/**
 * @deprecated Prefer governance_mode checks inline; limited external use.
 * Candidate for REMOVE AFTER V2 DEFAULT cleanup wave.
 */
export function isFlexibleGovernanceQuota(q: ExperienceQuotaFields | null): boolean {
  if (!q) return false;
  return q.governance_mode !== 'strict';
}

/**
 * Whether UI billing (usage/start) may begin for a live experience session.
 * Flexible governance with quota_exhausted=false may start when remaining_seconds is null.
 */
/**
 * @deprecated Quota Runtime V1 — Start Class / session cap from daily remaining.
 * KEEP while V2 flag is off. Remove after V2 is default.
 * Replacement: ``useQuotaRuntimeV2.ensureSession`` + monthly remaining / ``expires_at``.
 */
export function resolveExperienceBillingStartParams(
  q: ExperienceQuotaFields | null,
): ExperienceBillingStartParams {
  const governanceMode: 'flexible' | 'strict' | null =
    q?.governance_mode === 'strict' ? 'strict' : q ? 'flexible' : null;

  if (!q) {
    return {
      canStart: false,
      sessionCapSeconds: null,
      remainingForStartApi: null,
      governanceMode: null,
      blockReason: 'unknown_remaining',
    };
  }

  if (isExperienceQuotaExhausted(q)) {
    return {
      canStart: false,
      sessionCapSeconds: 0,
      remainingForStartApi: 0,
      governanceMode,
      blockReason: 'exhausted',
    };
  }

  const display = resolveExperienceQuotaDisplay(q, 0);
  const remaining = display.remainingSeconds;

  if (governanceMode === 'flexible' && q.quota_exhausted !== true && remaining == null) {
    return {
      canStart: true,
      sessionCapSeconds: null,
      remainingForStartApi: null,
      governanceMode: 'flexible',
      blockReason: null,
    };
  }

  if (remaining == null) {
    return {
      canStart: false,
      sessionCapSeconds: null,
      remainingForStartApi: null,
      governanceMode,
      blockReason: 'unknown_remaining',
    };
  }

  if (remaining <= 0) {
    return {
      canStart: false,
      sessionCapSeconds: 0,
      remainingForStartApi: 0,
      governanceMode,
      blockReason: 'exhausted',
    };
  }

  return {
    canStart: true,
    sessionCapSeconds: remaining,
    remainingForStartApi: remaining,
    governanceMode,
    blockReason: null,
  };
}

export function usesStrictChannelQuotaSlice(q: ExperienceQuotaFields | null): boolean {
  if (!q) return false;
  if (hasPreferredAllocationSlice(q)) return false;
  return (
    q.usage_scope === 'pooled' &&
    q.governance_mode === 'strict' &&
    q.channel_daily_limit_seconds != null
  );
}

function hasPreferredAllocationSlice(q: ExperienceQuotaFields | null): boolean {
  const pref = q?.preferred_allocation;
  if (!pref) return false;
  return (pref.daily_minutes_allocated ?? 0) > 0;
}

/** School strict slice for a feature (authoritative when backend sends preferred_allocation). */
function resolvePreferredAllocationQuotaDisplay(
  q: ExperienceQuotaFields,
  livePendingSec: number,
): ExperienceQuotaDisplay | null {
  const pref = q.preferred_allocation;
  if (!pref || (pref.daily_minutes_allocated ?? 0) <= 0) return null;

  const allocatedSeconds = Math.max(0, Math.round(pref.daily_minutes_allocated * 60));
  const prefUsed =
    pref.daily_used_seconds ??
    Math.max(0, Math.round((pref.daily_used_minutes ?? 0) * 60));
  const channelUsed =
    q.usage_scope === 'pooled' && q.channel_daily_used_seconds != null
      ? Math.max(0, Math.floor(q.channel_daily_used_seconds))
      : 0;
  const serverUsed = Math.max(prefUsed, channelUsed);
  const usedSeconds = serverUsed + Math.max(0, livePendingSec);

  let remainingSeconds: number | null =
    pref.daily_remaining_seconds ??
    (pref.daily_remaining_minutes != null
      ? Math.max(0, Math.round(pref.daily_remaining_minutes * 60))
      : null);
  if (channelUsed > prefUsed) {
    remainingSeconds = Math.max(0, allocatedSeconds - serverUsed);
  } else if (remainingSeconds == null) {
    remainingSeconds = Math.max(0, allocatedSeconds - serverUsed);
  }
  remainingSeconds = Math.max(0, remainingSeconds - Math.max(0, livePendingSec));

  return { usedSeconds, remainingSeconds, allocatedSeconds };
}

/** Student-facing used/limit for today (preferred slice → strict channel → pooled daily). */
export function resolveExperienceQuotaDisplay(
  q: ExperienceQuotaFields | null,
  livePendingSec = 0,
): ExperienceQuotaDisplay {
  if (!q) {
    return { usedSeconds: 0, remainingSeconds: null, allocatedSeconds: null };
  }

  const preferred = resolvePreferredAllocationQuotaDisplay(q, livePendingSec);
  if (preferred) return preferred;

  const useStrictSliceToday = usesStrictChannelQuotaSlice(q);
  const allocated = useStrictSliceToday
    ? q.channel_daily_limit_seconds!
    : q.daily_allocated_seconds ?? null;
  const serverUsed = useStrictSliceToday
    ? q.channel_daily_used_seconds ?? 0
    : q.daily_used_seconds ?? 0;
  const usedSeconds = serverUsed + Math.max(0, livePendingSec);

  let remainingSeconds: number | null;
  if (useStrictSliceToday) {
    remainingSeconds =
      allocated != null ? Math.max(0, allocated - usedSeconds) : null;
  } else {
    remainingSeconds = q.daily_remaining_seconds ?? null;
    if (allocated != null) {
      remainingSeconds = Math.max(0, allocated - usedSeconds);
    } else if (livePendingSec > 0 && remainingSeconds != null) {
      remainingSeconds = Math.max(0, remainingSeconds - livePendingSec);
    }
  }

  return { usedSeconds, remainingSeconds, allocatedSeconds: allocated };
}

/**
 * @deprecated Quota Runtime V1 — unused externally; prefer ``resolveExperienceQuotaDisplay``.
 * Candidate for REMOVE AFTER V2 DEFAULT (or sooner if confirmed unused).
 */
export function resolveRemainingSecondsFromQuota(
  q: TutorQuotaDailyFields | null,
  livePendingSec = 0,
): number | null {
  return resolveExperienceQuotaDisplay(q, livePendingSec).remainingSeconds;
}

/**
 * @deprecated Quota Runtime V1 Start Class gate (daily + monthly).
 * KEEP while ``quota_runtime_v2_enabled`` is false.
 * Remove after V2 is default as Start Class authority.
 * Replacement: ``isRuntimeV2StartBlocked`` (``quotaRuntimeV2Display.ts``).
 */
export function isExperienceQuotaExhausted(q: ExperienceQuotaFields | null): boolean {
  if (!q) return false;
  if (q.feature_disabled === true) return true;

  if (hasPreferredAllocationSlice(q)) {
    const display = resolveExperienceQuotaDisplay(q, 0);
    if (display.remainingSeconds != null && display.remainingSeconds <= 0) {
      return true;
    }
    return false;
  }

  if (usesStrictChannelQuotaSlice(q)) {
    const display = resolveExperienceQuotaDisplay(q, 0);
    if (display.remainingSeconds != null && display.remainingSeconds <= 0) {
      return true;
    }
    if (q.channel_monthly_limit_seconds != null) {
      const monthlyUsed = q.channel_monthly_used_seconds ?? 0;
      if (monthlyUsed >= q.channel_monthly_limit_seconds) return true;
    }
    return false;
  }

  if (q.quota_exhausted === true) return true;
  const remaining = resolveExperienceQuotaDisplay(q, 0).remainingSeconds;
  if (remaining != null && remaining <= 0) return true;
  const mrSec =
    q.monthly_remaining_seconds ??
    (q.monthly_remaining_minutes != null ? q.monthly_remaining_minutes * 60 : null);
  if (mrSec != null && mrSec <= 0) return true;
  return false;
}

/**
 * @deprecated Quota Runtime V1 daily-only exhaust helper.
 * KEEP for V1 tests/compat. Remove after V2 is default.
 */
export function experienceQuotaDailyExhausted(q: ExperienceQuotaFields | null): boolean {
  const remaining = resolveExperienceQuotaDisplay(q, 0).remainingSeconds;
  return remaining != null && remaining <= 0;
}

export function experienceQuotaMonthlyExhausted(q: ExperienceQuotaFields | null): boolean {
  if (!q) return false;
  if (usesStrictChannelQuotaSlice(q) && q.channel_monthly_limit_seconds != null) {
    const monthlyUsed = q.channel_monthly_used_seconds ?? 0;
    return monthlyUsed >= q.channel_monthly_limit_seconds;
  }
  const mrSec =
    q.monthly_remaining_seconds ??
    (q.monthly_remaining_minutes != null ? q.monthly_remaining_minutes * 60 : null);
  return mrSec != null && mrSec <= 0;
}

/** Keep the snapshot with higher used seconds (avoids stale bootstrap overwriting a fresh quota/me). */
export function mergeQuotaSnapshotsPreferHigherUsed(
  prev: StudentQuotaMeResponse | null,
  next: StudentQuotaMeResponse | null,
): StudentQuotaMeResponse | null {
  if (!next) return prev;
  if (!prev) return next;
  const prevUsed = resolveExperienceQuotaDisplay(prev, 0).usedSeconds;
  const nextUsed = resolveExperienceQuotaDisplay(next, 0).usedSeconds;
  return nextUsed >= prevUsed ? next : prev;
}

export function quotaMeFromBootstrapPayload(
  raw: Record<string, unknown> | null | undefined,
  featureName: string,
): StudentQuotaMeResponse | null {
  if (!raw || raw.ok === false) return null;
  return {
    ...(raw as StudentQuotaMeResponse),
    feature_name:
      typeof raw.feature_name === 'string' && raw.feature_name.trim()
        ? raw.feature_name
        : featureName,
  };
}

export function syncQuotaElapsedFromClock(
  snap: TutorSessionQuotaSnapshot | null,
  elapsedRef: { current: number },
  remainingRef: { current: number | null }
): { elapsed: number; remaining: number | null } {
  if (!snap) {
    const elapsed = Math.max(0, Math.floor(elapsedRef.current));
    return { elapsed, remaining: remainingRef.current };
  }
  const elapsed = Math.max(0, Math.floor((Date.now() - snap.sessionStartMs) / 1000));
  if (snap.remainingAtStart == null) {
    elapsedRef.current = elapsed;
    remainingRef.current = null;
    return { elapsed, remaining: null };
  }
  const remaining = Math.max(0, snap.remainingAtStart - elapsed);
  elapsedRef.current = elapsed;
  remainingRef.current = remaining;
  return { elapsed, remaining };
}

/**
 * Elapsed seconds for tab-close keepalive finalize.
 * Uses live clock from the session snap; falls back to usageStartedMs when snap/ref are stale.
 */
export function resolveUiTimerElapsedForKeepalive(
  snap: TutorSessionQuotaSnapshot | null,
  elapsedRef: { current: number },
  remainingRef: { current: number | null },
  usageStartedMs: number | null = null,
): number {
  const { elapsed } = syncQuotaElapsedFromClock(snap, elapsedRef, remainingRef);
  if (elapsed > 0) return elapsed;
  const startedMs = snap?.sessionStartMs ?? usageStartedMs;
  if (startedMs != null && startedMs > 0) {
    return Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
  }
  return 0;
}

export function buildQuotaMeFromFinalizeResult(
  result: TutorUsageFinalizeResult,
  prev: StudentQuotaMeResponse | null,
  defaultFeatureName: string
): StudentQuotaMeResponse {
  const allocated = result.allocated_seconds ?? prev?.daily_allocated_seconds ?? null;
  const used = Math.max(0, Math.floor(result.used_seconds));
  let remaining = result.remaining_seconds;
  if (remaining == null && allocated != null) {
    remaining = Math.max(0, allocated - used);
  }
  const base: StudentQuotaMeResponse = {
    feature_name: prev?.feature_name ?? defaultFeatureName,
    daily_used_seconds: used,
    daily_allocated_seconds: allocated,
    daily_remaining_seconds: remaining,
    monthly_allocated_seconds: prev?.monthly_allocated_seconds,
    monthly_used_seconds: prev?.monthly_used_seconds,
    monthly_remaining_seconds: prev?.monthly_remaining_seconds,
    quota_exhausted: prev?.quota_exhausted,
    usage_scope: prev?.usage_scope,
    governance_mode: prev?.governance_mode,
    feature_disabled: prev?.feature_disabled,
    channel_daily_used_seconds: prev?.channel_daily_used_seconds,
    channel_daily_limit_seconds: prev?.channel_daily_limit_seconds,
    channel_monthly_used_seconds: prev?.channel_monthly_used_seconds,
    channel_monthly_limit_seconds: prev?.channel_monthly_limit_seconds,
    preferred_allocation: prev?.preferred_allocation ?? null,
    shared_pool: prev?.shared_pool ?? null,
  };
  return patchQuotaMeUsageFields(base, used, remaining);
}

/** Keep student-facing quota fields (incl. preferred_allocation slice) in sync after usage. */
export function patchQuotaMeUsageFields(
  q: StudentQuotaMeResponse,
  usedSeconds: number,
  remainingSeconds: number | null,
): StudentQuotaMeResponse {
  const used = Math.max(0, Math.floor(usedSeconds));
  let remaining = remainingSeconds;
  const allocated = q.daily_allocated_seconds;
  if (remaining == null && allocated != null) {
    remaining = Math.max(0, allocated - used);
  }

  const pref = q.preferred_allocation;
  const nextPref = pref
    ? {
        ...pref,
        daily_used_seconds: used,
        daily_used_minutes: Math.floor(used / 60),
        daily_remaining_seconds: remaining,
        daily_remaining_minutes:
          remaining != null ? Math.floor(remaining / 60) : pref.daily_remaining_minutes ?? null,
      }
    : null;

  return {
    ...q,
    daily_used_seconds: used,
    daily_remaining_seconds: remaining,
    preferred_allocation: nextPref,
    ...(q.usage_scope === "pooled" &&
    q.governance_mode === "strict" &&
    q.channel_daily_limit_seconds != null
      ? { channel_daily_used_seconds: used }
      : {}),
    quota_exhausted:
      remaining != null && remaining <= 0 ? true : q.quota_exhausted,
  };
}

export function buildOptimisticQuotaFromSessionSnap(
  prev: StudentQuotaMeResponse | null,
  snap: TutorSessionQuotaSnapshot,
  elapsedSeconds: number,
  defaultFeatureName: string,
): StudentQuotaMeResponse | null {
  if (!prev) return null;
  const elapsed = Math.max(0, Math.floor(elapsedSeconds));
  const used = snap.usedAtStart + elapsed;
  const remaining =
    snap.remainingAtStart == null
      ? null
      : Math.max(0, snap.remainingAtStart - elapsed);
  return patchQuotaMeUsageFields(
    { ...prev, feature_name: prev.feature_name || defaultFeatureName },
    used,
    remaining,
  );
}

/**
 * @deprecated Quota Runtime V1 daily invariant check.
 * KEEP for V1. Prefer a single shared implementation (TeacherLive has a local duplicate).
 * Remove after V2 is default.
 */
export function isQuotaDailyInvariantOk(data: TutorQuotaDailyFields): boolean {
  const used = data.daily_used_seconds ?? 0;
  const allocated = data.daily_allocated_seconds;
  const remaining = data.daily_remaining_seconds;
  if (allocated == null || remaining == null) return true;
  return used + remaining === allocated;
}
